import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, TextField, Switch, Button, Link, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert, Snackbar } from '@mui/material';
import axios from 'axios';

const ToolCard = ({ title, idLabel, enabled, id, onToggle, onChange, onSave, loading }) => {
  const [helpOpen, setHelpOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const getHelpContent = (toolTitle) => {
    switch(toolTitle) {
      case 'Google Analytics':
        return {
          title: 'Google Analytics Setup',
          content: 'To set up Google Analytics:\n1. Create a Google Analytics account\n2. Set up a new property\n3. Get your Measurement ID (format: G-XXXXXXXXXX)\n4. Add the ID to the field above\n5. Enable the toggle to activate tracking'
        };
      case 'Google Tag Manager':
        return {
          title: 'Google Tag Manager Setup', 
          content: 'To set up GTM:\n1. Create a GTM account\n2. Set up a new container\n3. Get your Container ID (format: GTM-XXXXXXX)\n4. Add the ID to the field above\n5. Enable the toggle to activate'
        };
      case 'Meta Pixel':
        return {
          title: 'Meta Pixel Setup',
          content: 'To set up Meta Pixel:\n1. Go to Facebook Business Manager\n2. Create a new Pixel\n3. Get your Pixel ID (15-digit number)\n4. Add the ID to the field above\n5. Enable the toggle to activate'
        };
      case 'LinkedIn Insight':
        return {
          title: 'LinkedIn Insight Tag Setup',
          content: 'To set up LinkedIn Insight:\n1. Go to LinkedIn Campaign Manager\n2. Create Insight Tag\n3. Get your Partner ID\n4. Add the ID to the field above\n5. Enable the toggle to activate'
        };
      case 'Pinterest Pixel':
        return {
          title: 'Pinterest Tag Setup',
          content: 'To set up Pinterest Tag:\n1. Go to Pinterest Ads\n2. Create conversion tag\n3. Get your Tag ID\n4. Add the ID to the field above\n5. Enable the toggle to activate'
        };
      case 'Snapchat Pixel':
        return {
          title: 'Snapchat Pixel Setup',
          content: 'To set up Snapchat Pixel:\n1. Go to Snapchat Ads Manager\n2. Create Pixel\n3. Get your Pixel ID\n4. Add the ID to the field above\n5. Enable the toggle to activate'
        };
      case 'Twitter Pixel':
        return {
          title: 'Twitter Pixel Setup',
          content: 'To set up Twitter Pixel:\n1. Go to Twitter Ads dashboard\n2. Create conversion tracking\n3. Get your Pixel ID\n4. Add the ID to the field above\n5. Enable the toggle to activate'
        };
      case 'TikTok Pixel':
        return {
          title: 'TikTok Pixel Setup',
          content: 'To set up TikTok Pixel:\n1. Go to TikTok Ads Manager\n2. Create Pixel\n3. Get your Pixel ID\n4. Add the ID to the field above\n5. Enable the toggle to activate'
        };
      case 'Google Ads Conversion':
        return {
          title: 'Google Ads Conversion Setup',
          content: 'To set up Google Ads Conversion:\n1. Go to Google Ads\n2. Create conversion action\n3. Get your Conversion ID\n4. Add the ID to the field above\n5. Enable the toggle to activate'
        };
      case 'Facebook Custom Audience':
        return {
          title: 'Facebook Custom Audience Setup',
          content: 'To set up Facebook Custom Audience:\n1. Go to Facebook Business Manager\n2. Create custom audience\n3. Get your Audience ID\n4. Add the ID to the field above\n5. Enable the toggle to activate'
        };
      case 'HubSpot Tracking':
        return {
          title: 'HubSpot Tracking Setup',
          content: 'To set up HubSpot Tracking:\n1. Go to HubSpot dashboard\n2. Get your Tracking ID\n3. Add the ID to the field above\n4. Enable the toggle to activate'
        };
      case 'Segment Analytics':
        return {
          title: 'Segment Analytics Setup',
          content: 'To set up Segment Analytics:\n1. Go to Segment dashboard\n2. Get your Write Key\n3. Add the Key to the field above\n4. Enable the toggle to activate'
        };
      default:
        return {
          title: `${toolTitle} Setup`,
          content: `Follow the official documentation to get your ${idLabel} and configure the tracking.`
        };
    }
  };

  const helpData = getHelpContent(title);

  const handleSave = async () => {
    setSaveLoading(true);
    try {
      await onSave();
      setSaveLoading(false);
    } catch (error) {
      setSaveLoading(false);
    }
  };

  return (
    <>
      <Card sx={{ mb: 3, borderRadius: 2, opacity: loading ? 0.6 : 1, transition: 'opacity 0.3s' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
            <Typography variant='h6'>{title}</Typography>
            <Switch 
              checked={enabled}
              onChange={(e) => onToggle(e.target.checked)}
              disabled={loading}
            />
          </Box>

          <Typography sx={{ fontSize: 14, mb: 1 }}>{idLabel}</Typography>

          <TextField
            fullWidth
            size='small'
            placeholder='Enter ID'
            value={id}
            onChange={(e) => onChange(e.target.value)}
            sx={{ mb: 2 }}
            disabled={loading}
          />

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Link 
              component='button'
              onClick={() => setHelpOpen(true)}
              underline='hover'
              sx={{ cursor: 'pointer' }}
              disabled={loading}
            >
              How It Work
            </Link>
            
            <Button 
              variant='contained'
              size='small'
              onClick={handleSave}
              disabled={loading || saveLoading}
              sx={{ ml: 'auto' }}
            >
              {saveLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>{helpData.title}</DialogTitle>
        <DialogContent>
          <Typography component='pre' sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
            {helpData.content}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

function MarketingTools() {
  const [tools, setTools] = useState({
    googleAnalytics: { enabled: false, id: '' },
    googleTagManager: { enabled: false, id: '' },
    metaPixel: { enabled: false, id: '' },
    linkedinInsight: { enabled: false, id: '' },
    pinterestPixel: { enabled: false, id: '' },
    snapchatPixel: { enabled: false, id: '' },
    twitterPixel: { enabled: false, id: '' },
    tiktokPixel: { enabled: false, id: '' },
    googleAdsConversion: { enabled: false, id: '' },
    facebookCustomAudience: { enabled: false, id: '' },
    hubspotTracking: { enabled: false, id: '' },
    segmentAnalytics: { enabled: false, id: '' },
  });

  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load initial data
  useEffect(() => {
    fetchMarketingTools();
  }, []);

  const fetchMarketingTools = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin-settings/marketing-tools');
      if (response.data && Object.keys(response.data).length > 0) {
        setTools(response.data);
      }
    } catch (error) {
      console.error('Error fetching marketing tools:', error);
      showSnackbar('Error loading marketing tools', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (toolKey) => {
    const newState = !tools[toolKey].enabled;
    setTools(prev => ({
      ...prev,
      [toolKey]: {
        ...prev[toolKey],
        enabled: newState
      }
    }));
    
    const status = newState ? 'enabled' : 'disabled';
    showSnackbar(`Tool ${status}`, 'success');
  };

  const handleChange = (toolKey, value) => {
    setTools(prev => ({
      ...prev,
      [toolKey]: {
        ...prev[toolKey],
        id: value
      }
    }));
  };

  const handleSave = async (toolKey) => {
    try {
      await axios.put('/api/admin-settings/marketing-tools', {
        [toolKey]: tools[toolKey]
      });
      showSnackbar(`${toolKey} updated successfully`, 'success');
    } catch (error) {
      console.error('Error saving marketing tool:', error);
      showSnackbar('Error saving marketing tool', 'error');
    }
  };

  const handleSaveAll = async () => {
    try {
      setLoading(true);
      await axios.put('/api/admin-settings/marketing-tools', tools);
      showSnackbar('All marketing tools updated successfully', 'success');
    } catch (error) {
      console.error('Error saving all marketing tools:', error);
      showSnackbar('Error saving marketing tools', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant='h5'>
          Marketing Tools
        </Typography>
        <Button 
          variant='contained' 
          color='primary'
          onClick={handleSaveAll}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save All'}
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          <ToolCard 
            title='Google Analytics' 
            idLabel='Measurement ID'
            enabled={tools.googleAnalytics.enabled}
            id={tools.googleAnalytics.id}
            onToggle={() => handleToggle('googleAnalytics')}
            onChange={(value) => handleChange('googleAnalytics', value)}
            onSave={() => handleSave('googleAnalytics')}
            loading={loading}
          />
          
          <ToolCard 
            title='Google Tag Manager' 
            idLabel='GTM Container ID'
            enabled={tools.googleTagManager.enabled}
            id={tools.googleTagManager.id}
            onToggle={() => handleToggle('googleTagManager')}
            onChange={(value) => handleChange('googleTagManager', value)}
            onSave={() => handleSave('googleTagManager')}
            loading={loading}
          />
          
          <ToolCard 
            title='Meta Pixel' 
            idLabel='Meta Pixel ID'
            enabled={tools.metaPixel.enabled}
            id={tools.metaPixel.id}
            onToggle={() => handleToggle('metaPixel')}
            onChange={(value) => handleChange('metaPixel', value)}
            onSave={() => handleSave('metaPixel')}
            loading={loading}
          />
          
          <ToolCard 
            title='LinkedIn Insight' 
            idLabel='LinkedIn Partner ID'
            enabled={tools.linkedinInsight.enabled}
            id={tools.linkedinInsight.id}
            onToggle={() => handleToggle('linkedinInsight')}
            onChange={(value) => handleChange('linkedinInsight', value)}
            onSave={() => handleSave('linkedinInsight')}
            loading={loading}
          />
          
          <ToolCard 
            title='Pinterest Pixel' 
            idLabel='Pinterest Tag ID'
            enabled={tools.pinterestPixel.enabled}
            id={tools.pinterestPixel.id}
            onToggle={() => handleToggle('pinterestPixel')}
            onChange={(value) => handleChange('pinterestPixel', value)}
            onSave={() => handleSave('pinterestPixel')}
            loading={loading}
          />
          
          <ToolCard 
            title='Snapchat Pixel' 
            idLabel='Snap Pixel ID'
            enabled={tools.snapchatPixel.enabled}
            id={tools.snapchatPixel.id}
            onToggle={() => handleToggle('snapchatPixel')}
            onChange={(value) => handleChange('snapchatPixel', value)}
            onSave={() => handleSave('snapchatPixel')}
            loading={loading}
          />
          
          <ToolCard 
            title='Twitter Pixel' 
            idLabel='Twitter Pixel ID'
            enabled={tools.twitterPixel.enabled}
            id={tools.twitterPixel.id}
            onToggle={() => handleToggle('twitterPixel')}
            onChange={(value) => handleChange('twitterPixel', value)}
            onSave={() => handleSave('twitterPixel')}
            loading={loading}
          />
          
          <ToolCard 
            title='TikTok Pixel' 
            idLabel='TikTok Pixel ID'
            enabled={tools.tiktokPixel.enabled}
            id={tools.tiktokPixel.id}
            onToggle={() => handleToggle('tiktokPixel')}
            onChange={(value) => handleChange('tiktokPixel', value)}
            onSave={() => handleSave('tiktokPixel')}
            loading={loading}
          />
          
          <ToolCard 
            title='Google Ads Conversion' 
            idLabel='Conversion ID'
            enabled={tools.googleAdsConversion.enabled}
            id={tools.googleAdsConversion.id}
            onToggle={() => handleToggle('googleAdsConversion')}
            onChange={(value) => handleChange('googleAdsConversion', value)}
            onSave={() => handleSave('googleAdsConversion')}
            loading={loading}
          />
          
          <ToolCard 
            title='Facebook Custom Audience' 
            idLabel='Audience ID'
            enabled={tools.facebookCustomAudience.enabled}
            id={tools.facebookCustomAudience.id}
            onToggle={() => handleToggle('facebookCustomAudience')}
            onChange={(value) => handleChange('facebookCustomAudience', value)}
            onSave={() => handleSave('facebookCustomAudience')}
            loading={loading}
          />
          
          <ToolCard 
            title='HubSpot Tracking' 
            idLabel='HubSpot ID'
            enabled={tools.hubspotTracking.enabled}
            id={tools.hubspotTracking.id}
            onToggle={() => handleToggle('hubspotTracking')}
            onChange={(value) => handleChange('hubspotTracking', value)}
            onSave={() => handleSave('hubspotTracking')}
            loading={loading}
          />
          
          <ToolCard 
            title='Segment Analytics' 
            idLabel='Segment Write Key'
            enabled={tools.segmentAnalytics.enabled}
            id={tools.segmentAnalytics.id}
            onToggle={() => handleToggle('segmentAnalytics')}
            onChange={(value) => handleChange('segmentAnalytics', value)}
            onSave={() => handleSave('segmentAnalytics')}
            loading={loading}
          />
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default MarketingTools;
